import { assignInlineVars } from '@vanilla-extract/dynamic';
import {
  Children,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import * as styles from './index.css';

export const ANIMATED_PRESENCE_DURATION_MS = 180;

type PresencePhase = 'entering' | 'present' | 'exiting';

type ChildItem = {
  child: ReactNode;
  key: string;
};

type PresenceEntry = {
  exitingChild: ReactNode;
  key: string;
  phase: PresencePhase;
};

function getChildren(children: ReactNode): ChildItem[] {
  return Children.toArray(children).map((child, index) => ({
    child,
    key: String((child as { key?: string | null }).key ?? index),
  }));
}

function entriesEqual(a: PresenceEntry[], b: PresenceEntry[]) {
  return (
    a.length === b.length &&
    a.every((entry, index) => {
      const other = b[index];
      return other?.key === entry.key && other.phase === entry.phase;
    })
  );
}

function mergeEntries({
  current,
  lastChildByKey,
  mounted,
  previous,
}: {
  current: ChildItem[];
  lastChildByKey: Map<string, ReactNode>;
  mounted: boolean;
  previous: PresenceEntry[];
}): PresenceEntry[] {
  const currentByKey = new Map(current.map(item => [item.key, item]));
  const previousByKey = new Map(previous.map(item => [item.key, item]));
  const previousIndexByKey = new Map(
    previous.map((item, index) => [item.key, index])
  );

  const nextEntries: PresenceEntry[] = current.map(item => {
    const previousEntry = previousByKey.get(item.key);
    return {
      key: item.key,
      phase:
        mounted && !previousEntry
          ? ('entering' as const)
          : ('present' as const),
      exitingChild: item.child,
    };
  });

  const removedEntries = previous
    .filter(entry => !currentByKey.has(entry.key))
    .map(entry => ({
      ...entry,
      exitingChild: lastChildByKey.get(entry.key) ?? entry.exitingChild,
      phase: 'exiting' as const,
    }));

  const merged = [...nextEntries];
  for (const removed of removedEntries) {
    const oldIndex = previousIndexByKey.get(removed.key) ?? merged.length;
    const insertAt = merged.findIndex(entry => {
      const entryOldIndex = previousIndexByKey.get(entry.key);
      return entryOldIndex !== undefined && entryOldIndex > oldIndex;
    });
    merged.splice(insertAt === -1 ? merged.length : insertAt, 0, removed);
  }

  return merged;
}

const PresenceItem = ({
  children,
  dataAttribute,
  defaultHeight,
  phase,
}: {
  children: ReactNode;
  dataAttribute?: string;
  defaultHeight: number;
  phase: PresencePhase;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(defaultHeight);
  const dataAttributes = dataAttribute ? { [dataAttribute]: 'true' } : {};

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }

    const measuredHeight =
      content.scrollHeight ||
      content.getBoundingClientRect().height ||
      defaultHeight;
    setHeight(measuredHeight);
  }, [children, defaultHeight, phase]);

  return (
    <div
      className={styles.presenceItem}
      data-presence={phase}
      style={assignInlineVars({
        [styles.presenceHeight]: `${height}px`,
      })}
      {...dataAttributes}
    >
      <div className={styles.presenceContent} ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export const AnimatedPresenceList = ({
  children,
  defaultHeight = 42,
  itemDataAttribute,
}: {
  children?: ReactNode;
  defaultHeight?: number;
  itemDataAttribute?: string;
}) => {
  const currentChildren = getChildren(children);
  const keySignature = currentChildren.map(child => child.key).join('\u001f');
  const mountedRef = useRef(false);
  const lastChildByKeyRef = useRef(new Map<string, ReactNode>());
  const [entries, setEntries] = useState<PresenceEntry[]>(() =>
    currentChildren.map(item => ({
      key: item.key,
      phase: 'present',
      exitingChild: item.child,
    }))
  );

  const currentChildByKey = new Map(
    currentChildren.map(item => [item.key, item.child])
  );

  useEffect(() => {
    setEntries(previous => {
      const next = mergeEntries({
        current: currentChildren,
        lastChildByKey: lastChildByKeyRef.current,
        mounted: mountedRef.current,
        previous,
      });
      return entriesEqual(previous, next) ? previous : next;
    });
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySignature]);

  useEffect(() => {
    for (const child of currentChildren) {
      lastChildByKeyRef.current.set(child.key, child.child);
    }
  });

  useEffect(() => {
    if (!entries.some(entry => entry.phase !== 'present')) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setEntries(previous =>
        previous
          .filter(entry => entry.phase !== 'exiting')
          .map(entry => ({ ...entry, phase: 'present' }))
      );
    }, ANIMATED_PRESENCE_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, [entries]);

  return (
    <>
      {entries.map(entry => (
        <PresenceItem
          dataAttribute={itemDataAttribute}
          defaultHeight={defaultHeight}
          key={entry.key}
          phase={entry.phase}
        >
          {currentChildByKey.get(entry.key) ?? entry.exitingChild}
        </PresenceItem>
      ))}
    </>
  );
};
